'use client';

import { NotificationCategory } from '@/shared/lib/notifications/notification';
import { Icon } from '@phosphor-icons/react';
import { ChartLineUpIcon, UserIcon, WarningIcon, PulseIcon, UsersThreeIcon } from '@phosphor-icons/react/dist/ssr';
import cn from 'classnames';
import Link from 'next/link';
import { ACCOUNT_NOTIFICATION_PATHNAME } from '@/shared/constants/pathnames';
import { BellSlashIcon, ChecksIcon, GearIcon, XIcon, WrenchIcon, FileTextIcon } from '@phosphor-icons/react/dist/ssr';
import { forwardRef } from 'react';
import { Button, Text } from '@/shared/ui/core';
import { useNotificationsMenu } from '../lib/use-notifications-menu';
import styles from './notifications-menu.module.scss';


type ComponentProps = {
    toggleOpen: () => void
}


const CATEGORY_ICONS: Record<NotificationCategory, Icon> = {
    account_update    : UserIcon,
    analytics_report  : ChartLineUpIcon,
    new_incident      : WarningIcon,
    service_update    : FileTextIcon,
    system_maintenance: WrenchIcon,
    system_status     : PulseIcon,
    team              : UsersThreeIcon,
};
const CATEGORY_TYPES: Record<NotificationCategory, string> = {
    account_update    : 'Account update',
    analytics_report  : 'Analytics report',
    new_incident      : 'New incident',
    service_update    : 'Service update',
    system_maintenance: 'System maintenance',
    system_status     : 'System status',
    team              : 'Team update',
};


export const NotificationsMenu = forwardRef<HTMLDivElement, ComponentProps>(function NotificationsMenu ({ toggleOpen }, ref) {
    const { hasItems, hasUnread, items, markAllRead, openNotification } = useNotificationsMenu(toggleOpen);

    return (
        <div
            className={ styles.root }
            ref={ ref }
        >
            <div className={ styles.title }>
                <Text
                    size="xl"
                    tone="primary"
                    weight="semibold"
                    lineHeight={ 1.2 }
                >
                    Notifications
                </Text>

                <div className={ styles.title_icons }>
                    <Link
                        href={ ACCOUNT_NOTIFICATION_PATHNAME }
                        prefetch={ false }
                        onClick={ toggleOpen }
                    >
                        <GearIcon size={ 20 }/>
                    </Link>
                    <Button
                        variant="naked"
                        aria-label="Close notification menu"
                        onClick={ toggleOpen }
                    >
                        <XIcon size={ 20 }/>
                    </Button>
                </div>
            </div>

            {
                hasItems
                    ? (
                        <div className={ styles.list }>
                            {
                                items.map((n) => {
                                    const _Icon = CATEGORY_ICONS[n.category] ?? (() => null);

                                    return (
                                        <Button
                                            key={ n.id }
                                            variant="naked"
                                            className={ cn(styles.item, { [styles.new]: !n.read }) }
                                            data-read={ n.read }
                                            data-severity={ n.severity }
                                            onClick={ () => openNotification(n) }
                                        >
                                            <div className={ styles.icon_wrapper }>
                                                <_Icon size={ 32 }/>
                                            </div>

                                            <div className={ styles.item_content }>
                                                <Text
                                                    weight='medium'
                                                    lineHeight={ 1 }
                                                    className={ styles.item_category }
                                                >
                                                    { CATEGORY_TYPES[n.category] }
                                                </Text>

                                                <Text
                                                    size="md"
                                                    weight='medium'
                                                    className={ styles.item_text }
                                                >
                                                    { n.text ?? n.title }
                                                </Text>
                                            </div>

                                            {
                                                !n.read &&
                                                <div className={ styles.new_mark } />
                                            }
                                        </Button>
                                    );
                                })
                            }
                        </div>
                    )
                    : (
                        <div className={ styles.no_notifications }>
                            <BellSlashIcon
                                className={ styles.no_notifications_icon }
                                size={ 32 }
                            />

                            <Text
                                size="md"
                                weight="medium"
                                lineHeight={ 1.2 }
                                className={ styles.no_notifications_title }
                            >
                                Nothing to see here!
                            </Text>

                            <Text
                                size="md"
                                lineHeight={ 1.2 }
                                className={ styles.no_notifications_text }
                            >
                                You’re all caught up with your notifications
                            </Text>
                        </div>
                    )
            }


            {
                hasUnread &&
                <div className={ styles.footer }>
                    <Button
                        variant="naked"
                        className={ styles.mark_all }
                        onClick={ markAllRead }
                    >
                        <ChecksIcon size={ 12 }/>

                        <Text
                            as='span'
                            size='sm'
                            tone='secondary'
                            lineHeight={ 1 }
                        >
                            Mark all as read
                        </Text>
                    </Button>
                </div>
            }
        </div>
    );
});
